/**
 * Keeps React rendering after the browser's page translator rewrites the DOM.
 *
 * Chrome's and Safari's translators replace a text node React rendered with a
 * `<font>` wrapper holding the translation. When React later removes or moves
 * that text node, it calls `removeChild` or `insertBefore` on the old parent,
 * the DOM throws `NotFoundError`, and React unmounts the whole portal. Skipping
 * an operation whose node no longer sits under that parent leaves the
 * translated copy on screen and keeps the page alive. hire-go applies the
 * same guard.
 */
export function guardDomAgainstPageTranslation(): void {
  if (typeof Node !== 'function' || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) return child;

    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;

    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
